import { gql } from '@apollo/client';
import { BoxFooter, Button, Text, Textarea } from '@mnd-frontend/ui';
import type React from 'react';
import { useRef, useState } from 'react';
import { useCurrentPressroom } from '../../../../../contexts/CurrentPressroomContext';
import { useSupportFormQuery } from '../../../../../generated/hooks';
import useCopyToClipboard from '../../../../../hooks/useCopyToClipboard';

gql`
  query SupportForm($pressroomId: ID!) {
    pressroom(id: $pressroomId) {
      id
      name
      siteCode
      source {
        id
        name
        currentPlan
      }
    }
  }
`;

const getDebugText = ({
  currentSourceID,
  currentSourcename,
  currentPlan,
  frontendVersion,
  pressroomId,
  pressroomName,
  siteCode,
}: {
  currentSourceID?: string;
  currentSourcename?: string;
  currentPlan?: string;
  frontendVersion?: string;
  pressroomId?: string;
  pressroomName?: string;
  siteCode?: string;
}) => `${
  currentSourceID && `- Admin link: https://www.mynewsdesk.com/admin/source/edit/${currentSourceID}`
}
${currentPlan && `- Customer type: ${currentPlan}`}
${pressroomId && `- Newsroom ID: ${pressroomId} ${pressroomName}`}
${currentSourceID && `- Source ID: ${currentSourceID} (${currentSourcename})`}
${siteCode && `- Site code: ${siteCode}`}
- Current link: ${window?.location?.href}
${frontendVersion && `- Publish frontend version: ${frontendVersion}`}
- Time and date: ${Date().toLocaleString()}

Steps to reproduce:
[Problem description - step by step]
[1. Click create new...]
[2. Go to...]

Expected result:
[What is the expected result?]

Actual result:
[What happened?]

Can it be reproduced: [Y/N]
`;

type SupportFormProps = {
  onClose: () => void;
};

const FRONTEND_VERSION = (import.meta.env.VITE_BUILD_VERSION as string) ?? '';

const SupportForm: React.FC<SupportFormProps> = ({ onClose }) => {
  const [value, setValue] = useState('loading...');
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const [copied, copy, resetCopy] = useCopyToClipboard(value, inputRef);
  const { pressroomId } = useCurrentPressroom();

  useSupportFormQuery({
    variables: { pressroomId },
    onCompleted: data => {
      const frontendVersion = FRONTEND_VERSION;
      const currentPlan = data?.pressroom?.source?.currentPlan ?? '';
      const currentSourceID = data?.pressroom?.source?.id;
      const currentSourcename = data?.pressroom?.source?.name ?? '';
      const pressroomName = data?.pressroom?.name ? `(${data.pressroom.name})` : '';
      const siteCode = data?.pressroom?.siteCode;

      setValue(
        getDebugText({
          currentSourceID,
          currentSourcename,
          currentPlan,
          frontendVersion,
          pressroomId,
          pressroomName,
          siteCode,
        }),
      );
    },
    skip: !pressroomId,
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setValue(e.target.value);
  };

  const onCloseExtended = () => {
    onClose();
    resetCopy();
  };

  return (
    <>
      <div>
        <Textarea ref={inputRef} onChange={handleChange} rows={15} value={value} label="Info" />
      </div>
      <BoxFooter>
        {copied && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text color="RED_700" as="span" variant="labelMedium">
              Copied to clipboard!
            </Text>
          </div>
        )}
        <Button type="button" configuration="text" onClick={onCloseExtended}>
          Close
        </Button>
        <Button type="button" onClick={copy}>
          Copy all
        </Button>
      </BoxFooter>
    </>
  );
};

export default SupportForm;
